import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { get, post } from '../api';
import { useMe } from '../auth';
import { Alert, Empty, fmtRelative, PageHeader, Spinner, StatusBadge, useAction, useLoader } from '../components/ui';
import type { EndUser } from '../types';

export function EndUsersPage() {
  const { isAdmin } = useMe();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  useEffect(() => setSearch(q), [q]);

  const { data, error, loading, reload } = useLoader(
    () => get<{ end_users: EndUser[] }>('/api/end-users', { status, q, limit: 200 }),
    [status, q],
  );
  const act = useAction();

  const update = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    update('q', search.trim());
  };
  const unblock = (u: EndUser) =>
    act.run(u.external_id, async () => {
      await post(`/api/end-users/${encodeURIComponent(u.external_id)}/unblock`);
      await reload(true);
    });

  const users = data?.end_users ?? [];
  return (
    <>
      <PageHeader title="End users" subtitle="People on your platform, ordered by number of violations." />
      <section className="card filters">
        <form className="toolbar" onSubmit={onSearch}>
          <div className="segmented" role="group" aria-label="Status">
            {[
              ['', 'All'],
              ['blocked', 'Blocked'],
              ['active', 'Active'],
            ].map(([v, label]) => (
              <button type="button" key={v} className={status === v ? 'active' : ''} onClick={() => update('status', v)}>
                {label}
              </button>
            ))}
          </div>
          <input type="search" placeholder="Search user ID or name" value={search} onChange={(e) => setSearch(e.target.value)} className="grow" />
          <button className="btn">Search</button>
        </form>
      </section>
      <Alert>{error ?? act.error}</Alert>
      <section className="card card-flush">
        {loading && !data ? (
          <Spinner />
        ) : users.length === 0 ? (
          <Empty>No end users found.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th className="num">Messages</th>
                  <th className="num">Violations</th>
                  <th>Last violation</th>
                  <th>Last seen</th>
                  <th className="col-content">Block reason</th>
                  {isAdmin && <th />}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.external_id}>
                    <td className="nowrap">
                      <Link to={`/messages?user_id=${encodeURIComponent(u.external_id)}&include_allowed=1`} className="mono">
                        {u.external_id}
                      </Link>
                      {u.display_name && <div className="muted small">{u.display_name}</div>}
                    </td>
                    <td>
                      <StatusBadge status={u.status} />
                    </td>
                    <td className="num mono">{u.message_count}</td>
                    <td className="num mono">{u.violation_count}</td>
                    <td className="nowrap muted">{u.last_violation_at ? fmtRelative(u.last_violation_at) : '—'}</td>
                    <td className="nowrap muted">{fmtRelative(u.last_seen_at)}</td>
                    <td className="col-content small">{u.blocked_reason ?? <span className="muted">—</span>}</td>
                    {isAdmin && (
                      <td className="nowrap right">
                        {u.status === 'blocked' && (
                          <button className="btn btn-sm" disabled={act.busy === u.external_id} onClick={() => unblock(u)}>
                            {act.busy === u.external_id ? 'Unblocking…' : 'Unblock'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
